import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonDunder = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Dunder (Magic) Methods: The Cheat Codes</h1>
                <p className="subtitle">Unlock the full power of Python by hooking into its core syntax using secret spells.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Imagine you are a wizard in a video game. You have a staff (Common methods like `.attack()`).
                        But you also have a secret book of <strong>Ancient Spells</strong> (Dunder methods).
                    </p>
                    <ul>
                        <li><strong>Standard Method:</strong> `staff.shoot_fireball()`. You have to say the full name to use it.</li>
                        <li><strong>Magic Method:</strong> `__add__`. This is a passive spell. If you just wave your staff (write `+`), the spell activates automatically!</li>
                    </ul>
                    <p>
                        Python has many of these hidden spells that start and end with two underscores (like `__init__`).
                        If you write them in your class, Python's "Game Engine" will call them automatically when you use symbols like `+`, `-`, `==`, or `len()`.
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>2. String Representation: `__str__` vs `__repr__`</h2>
                <p>
                    When you catach a Pokemon and look at it in your Pokedex, you want to see a nice name, not a serial number.
                </p>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Method</th>
                            <th>Audience</th>
                            <th>Goal</th>
                            <th>Example Output</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>`__str__`</td>
                            <td>End User</td>
                            <td>Readability</td>
                            <td>`"Ace of Spades"`</td>
                        </tr>
                        <tr>
                            <td>`__repr__`</td>
                            <td>Developer</td>
                            <td>Unambiguity & Debugging</td>
                            <td>`Card(rank='Ace', suit='Spades')`</td>
                        </tr>
                    </tbody>
                </table>

                <CodeBlock language="python" code={`class Pokemon:
    def __init__(self, name, level):
        self.name = name
        self.level = level

    def __str__(self):
        # Friendly for players
        return f"{self.name} (Lvl {self.level})"

    def __repr__(self):
        # Precise for debugging
        return f"Pokemon(name='{self.name}', level={self.level})"

p = Pokemon("Charizard", 36)

print(p)       # Charizard (Lvl 36) -&gt; Automatically calls __str__
print([p])     # [Pokemon(name='Charizard', level=36)] -&gt; Lists use __repr__`} />
                <ProTip><p><strong>Fallback Rule:</strong> If you don't define `__str__`, Python will look for `__repr__`. If that's missing too, you get the ugly `&lt;__main__.Pokemon object at 0x...&gt;`.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>3. Operator Overloading: The Math Protocol</h2>
                <p>
                    Why does `1 + 2 = 3` but `"a" + "b" = "ab"`?
                    It's because `int` and `str` have defined their own `__add__` spell!
                    Let's teach our Pokemon how to fight using math symbols.
                </p>

                <CodeBlock language="python" code={`class Vector:
    def __init__(self, x, y):
        self.x = x
        self.y = y

    # The '+' Spell
    def __add__(self, other):
        # Triggered by: v1 + v2
        return Vector(self.x + other.x, self.y + other.y)

    def __mul__(self, scalar):
        # Triggered by: v1 * 3
        return Vector(self.x * scalar, self.y * scalar)

    def __eq__(self, other):
        # Triggered by: v1 == v2
        return self.x == other.x and self.y == other.y

    def __repr__(self):
        return f"Vector({self.x}, {self.y})"

v1 = Vector(2, 4)
v2 = Vector(1, 1)

v3 = v1 + v2       # Vector(3, 5)
print(v3 == Vector(3, 5)) # True`} />

                <p>
                    <strong>Power with Responsibility:</strong> Overloading allows expressive code (e.g., `path / "file.txt"` in standard library's `pathlib`).
                    However, don't abuse it. If `Player + Player` breeds a new child, that makes sense.
                    If `Player + Player` kills both players, that is confusing.
                    Keep the semantics intuitive.
                </p>

                <ProTip><p><strong>Limitation:</strong> You cannot overload `and`, `or`, or `not`. Python enforces short-circuit logic for these keywords. You can only overload Bitwise operators (`&`, `|`, `~`).</p></ProTip>
            </div>

            <div className="content-section">
                <h2>4. The Container Protocol: `__len__`, `__getitem__`</h2>
                <p>
                    Want your object to act like a List or Dictionary?
                    You just need two spells:
                </p>
                <ul>
                    <li>`__len__`: Tells Python "How many items do I have?"</li>
                    <li>`__getitem__`: Tells Python "Give me the item at this slot."</li>
                </ul>

                <CodeBlock language="python" code={`class Backpack:
    def __init__(self):
        self.items = []

    def add(self, item):
        self.items.append(item)

    # 1. Allow 'len(backpack)'
    def __len__(self):
        return len(self.items)

    # 2. Allow 'backpack[0]'
    def __getitem__(self, index):
        return self.items[index]

my_bag = Backpack()
my_bag.add("Potion")
my_bag.add("Map")

print(len(my_bag)) # 2
print(my_bag[0])   # Potion

# Magic: Because we have getitem, we can loop!
for item in my_bag:
    print(item) # Prints Potion, then Map`} />
            </div>

            <div className="content-section">
                <h2>Why the Container Protocol matters</h2>
                <p>
                    By implementing just these two methods, your object becomes "Duck Typed" as a sequence.
                    You can use it in `for` loops, list comprehensions, and even pass it to functions that expect lists.
                    This is polymorphism in actionPython doesn't check if it <em>is</em> a list, only if it <em>behaves</em> like one.
                </p>
            </div>


            <div className="content-section">
                <h2>5. Context Managers: `__enter__` & `__exit__`</h2>
                <p>
                    The `with` statement is like entering a <strong>Safe Room</strong>.
                    When you enter (`__enter__`), the lights turn on.

                    When you leave (`__exit__`), the lights turn off <em>automatically</em>, even if you trip and fall (raise an error) inside.
                    This is known as <strong>Resource Management</strong>. It is essential for:
                </p>
                <ul className="premium-list">
                    <li><strong>File Operations:</strong> Closing the file handle.</li>
                    <li><strong>Network Connections:</strong> Disconnecting from a database.</li>
                    <li><strong>Locks:</strong> Releasing a thread lock to prevent deadlocks.</li>
                </ul>
                <CodeBlock language="python" code={`import time

class Timer:
    def __enter__(self):
        self.start = time.time()
        return self 

    def __exit__(self, exc_type, exc_val, exc_tb):
        self.end = time.time()
        print(f" Execution Time: {self.end - self.start:.4f} seconds")
        # Return False to let exceptions propagate
        return False

with Timer():
    # Run some heavy code
    sum([i**2 for i in range(100000)])
    
# Output:  Execution Time: 0.0345 seconds`} />
            </div>

            <div className="content-section">
                <h2>6. Making Objects Callable: `__call__`</h2>
                <p>
                    Normally, you call functions `my_func()` and objects are just things `my_obj`.
                    But with `__call__`, you can make an object pretend to be a function!
                </p>
                <CodeBlock language="python" code={`class Multiplier:
    def __init__(self, factor):
        self.factor = factor

    def __call__(self, number):
        return number * self.factor

double = Multiplier(2)
triple = Multiplier(3)

print(double(10)) # 20 (It looks like a function!)
print(triple(10)) # 30`} />
            </div>

            <div className="content-section">
                <h2>7. Iterators: `__iter__` vs `__next__`</h2>
                <p>
                    While `__getitem__` allows simple iteration, powerful iteration requires the <strong>Iterator Protocol</strong>.
                </p>
                <CodeBlock language="python" code={`class CountDown:
    def __init__(self, start):
        self.current = start

    def __iter__(self):
        return self

    def __next__(self):
        if self.current <= 0:
            raise StopIteration
        
        num = self.current
        self.current -= 1
        return num

for n in CountDown(3):
    print(n) # 3, 2, 1`} />
            </div>



            <div className="content-section">
                <h2>9. Extended Technical Reference</h2>

                <h3>Operator Overloading: Python vs The World</h3>
                <p>
                    Python's "Dunder" (Double Underscore) system is often cited as one of its most powerful features.
                </p>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Language</th>
                            <th>Operator Overloading?</th>
                            <th>Mechanism</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Python</strong></td>
                            <td>Yes</td>
                            <td>Dunder Methods (`__add__`)</td>
                        </tr>
                        <tr>
                            <td><strong>C++</strong></td>
                            <td>Yes</td>
                            <td>`operator+` function</td>
                        </tr>
                        <tr>
                            <td><strong>Java</strong></td>
                            <td> No</td>
                            <td>Must use methods (`.add()`) - except `+` for Strings.</td>
                        </tr>
                        <tr>
                            <td><strong>JavaScript</strong></td>
                            <td> No</td>
                            <td>Implicit coercion (often buggy)</td>
                        </tr>
                    </tbody>
                </table>

                <h3>Deep Dive: `NotImplemented`</h3>
                <p>
                    What happens if you try to add `Circle + Square`?
                    Inside `Circle.__add__`, if you don't know how to handle a `Square`, you should return `NotImplemented` (not raise an Error!).
                    This gives Python a chance to ask the `Square` via `__radd__` (Right-Side Add) if <em>it</em> knows how to handle the Circle.
                    If both return `NotImplemented`, <em>then</em> Python raises a `TypeError`.
                </p>

                <h3>Common Dunder Cheat Sheet</h3>
                <ul className="premium-list">
                    <li>`__new__`: Use for immutable types (Metaclass magic).</li>
                    <li>`__del__`: Destructor (Don't rely on it!).</li>
                    <li>`__contains__`: For `in` operator (e.g., `if x in collection`).</li>
                    <li>`__bool__`: For boolean context (e.g., `if obj:`).</li>
                </ul>
            </div>

        </div >
    );
};

export default PythonDunder;


